import { useQueries } from '@tanstack/react-query'
import { IMyConnectorsDTO } from 'data/dtos/automation/i-custom-connector-dto'
import { PropsWithChildren, createContext, useContext } from 'react'
import { BaseResponse, loadList, loadSingle } from 'services/requests'
import { useAuth } from './auth'

interface IAuthenticationData {
  validate: string | null
  connectors: IMyConnectorsDTO[]
  isLoading: boolean
  refetchValidate: () => void
}

interface ICertificateValidate {
  validate: string
}

const AuthenticationsContext = createContext<IAuthenticationData>({} as IAuthenticationData)

const defineConnectorsContent = (connectors: IMyConnectorsDTO[], userRole: 'seller' | 'buyer') => {
  const { VITE_APP_AMBIENT } = import.meta.env

  if (VITE_APP_AMBIENT === 'prod') {
    if (userRole === 'buyer') {
      connectors = connectors.filter(
        (connector: IMyConnectorsDTO) => connector.key === 'dados-cadastrais' || connector.key === 'email'
      )
    } else {
      connectors = connectors.filter((connector: IMyConnectorsDTO) => connector.key === 'email')
    }
  }

  if (VITE_APP_AMBIENT !== 'prod') {
    if (userRole === 'buyer') {
      connectors = connectors.map((connector: IMyConnectorsDTO) => {
        if (connector.key == 'qbem') {
          connector.name = 'Convenia'
        } else if (connector.key == 'service-now') {
          connector.name = 'Slack'
        }

        return connector
      })
    } else {
      connectors = connectors.filter((connector: IMyConnectorsDTO) => connector.key !== 'dados-cadastrais')
    }
  }

  return connectors
}

const AuthenticationsProvider = ({ children }: PropsWithChildren) => {
  const { user, isAuthenticated } = useAuth()
  let userRole: 'seller' | 'buyer' = 'buyer'

  if (user && user.isSeller != undefined) {
    userRole = user.isSeller ? 'seller' : 'buyer'
  }

  const [content, validate] = useQueries({
    queries: [
      {
        queryKey: ['connectors', 'list', 'me'],
        queryFn: () =>
          loadList('/connectors/list/me', {
            search: '',
            page: 0,
            rowsPerPage: 1000,
            columnOrder: ['DESC'],
            organizationId: '',
          }),
        initialData: { data: { data: [] } } as any,
        enabled: isAuthenticated,
      },
      {
        queryKey: ['connectors', 'validate', 'me'],
        queryFn: () => loadSingle<BaseResponse<ICertificateValidate>>('/users/certificate/validate'),
        retry: false,
        enabled: isAuthenticated,
      },
    ],
  })

  let data = content.data?.data.data

  if (!content.isLoading) {
    data = defineConnectorsContent(data, userRole)
  }

  let validateString = null

  const validateObj = validate.data?.data.data

  if (validateObj?.validate) {
    validateString = validateObj.validate
  }

  return (
    <AuthenticationsContext.Provider
      value={{
        validate: validateString,
        connectors: data,
        refetchValidate: validate.refetch,
        isLoading: content.isLoading || content.isFetching || validate.isLoading || validate.isFetching,
      }}
    >
      {children}
    </AuthenticationsContext.Provider>
  )
}

const useAuthentications = (): IAuthenticationData => useContext(AuthenticationsContext)

export { AuthenticationsProvider, useAuthentications }
